/**
 * The node's pins (its ways out in the blueprint; a scene's choices when played): each one's
 * words, where it leads, and a button to take it away; "Add a choice" adds one. The same edits
 * as dragging in the blueprint (`state/pinEdits.ts`), for the keyboard. Shown when the node has
 * pins or the environment offers the blueprint.
 */
import { For, Show, createMemo, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { wireFrom } from "../../state/diagram";
import { pinEdits } from "../../state/pinEdits";
import { Fold } from "./Fold";

/** The "Pins" section of the node inspector. */
export function NodePins(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { graph, actions, kinds, play, view, access } = useApp();
  const pins = pinEdits({ graph, actions, kinds, play, view });
  const canEdit = () => access.can("editor");
  /** Where a pin may lead: every other node, by name. */
  const targets = createMemo(() => graph.state.graph.nodes.filter((o) => o.id !== n().id).sort((a, b) => a.name.localeCompare(b.name)));
  const offered = () => (n().pins?.length ?? 0) > 0 || view.views().includes("blueprint");
  return (
    <Show when={offered()}>
      <Fold id="pins" title="Choices" count={n().pins?.length ?? 0} empty={!n().pins?.length && canEdit()} add="Add a choice">
        <div class="stack node-pins" id="node-pins">
          <For each={n().pins ?? []}>
            {(pin) => (
              <div class="row-inline pin-row" data-pin={pin.id}>
                <input
                  class="field"
                  data-pin-label={pin.id}
                  aria-label="The choice's words"
                  value={pin.label}
                  placeholder={pins.label(n().id, pin)}
                  disabled={!canEdit()}
                  onChange={(e) => void pins.rename(n().id, pin.id, e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                />
                <select
                  class="select"
                  data-pin-to={pin.id}
                  aria-label="Where it leads"
                  disabled={!canEdit()}
                  onChange={(e) => {
                    const to = e.currentTarget.value;
                    void (to ? pins.wire(n().id, pin.id, to) : pins.disconnect(n().id, pin.id));
                  }}
                >
                  <option value="" selected={!wireFrom(graph.state.graph.links, n().id, pin.id)}>
                    Leads nowhere yet
                  </option>
                  <For each={targets()}>
                    {(t) => (
                      <option value={t.id} selected={wireFrom(graph.state.graph.links, n().id, pin.id)?.to === t.id}>
                        → {t.name}
                      </option>
                    )}
                  </For>
                </select>
                <button type="button" class="btn icon" data-pin-remove={pin.id} title="Remove this choice and its wire" disabled={!canEdit()} onClick={() => void pins.remove(n().id, pin.id)}>
                  ×
                </button>
              </div>
            )}
          </For>
          <button type="button" class="btn" id="node-pin-add" disabled={!canEdit()} onClick={() => void pins.add(n().id)}>
            Add a choice
          </button>
        </div>
      </Fold>
    </Show>
  );
}
